/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Typography } from '@mui/material';
import type { Feature } from 'geojson';
import type { View } from 'ol';
import React from 'react';
import { PROJECTION } from '@opengeoweb/shared';
import { LayerType, webmapUtils } from '@opengeoweb/webmap';
import type { OpenLayersLayerProps } from '@opengeoweb/webmap-react';
import {
  genericOpenLayersFeatureStyle,
  MapControls,
  OpenLayersFeatureLayer,
  OpenLayersLayer,
  OpenLayersMapView,
  OpenLayersZoomControl,
} from '@opengeoweb/webmap-react';

interface AreaFilterProps {
  view: View;
  tileURL: string;
  tileLicense: string;
  feature?: Feature;
}

export const AreaFilter: React.FC<AreaFilterProps> = ({
  view,
  tileURL,
  tileLicense,
  feature,
}: AreaFilterProps) => {
  const worldBbox = [-20037508.34, -20048966.1, 20037508.34, -20048966.1];

  const props = {
    isFocused: false,
    view,
  };

  const baseLayer: OpenLayersLayerProps = React.useMemo(() => {
    return {
      tileServer: {
        osm: {
          [PROJECTION.EPSG_3857.value]: {
            home: tileURL,
            minLevel: 0,
            maxLevel: 9,
            tileServerType: 'osm',
            copyRight: tileLicense,
          },
        },
      },
      name: 'osm',
      title: 'osm',
      type: 'twms',
      layerType: LayerType.baseLayer,
      enabled: true,
      id: `tile_${webmapUtils.generateLayerId()}`,
    };
  }, [tileURL, tileLicense]);

  return (
    <Box style={{ width: '100%', paddingLeft: 12, paddingRight: 12 }}>
      <Box style={{ height: '190px', width: '100%' }}>
        <OpenLayersMapView {...props}>
          <MapControls style={{ top: 0 }}>
            <OpenLayersZoomControl homeExtent={worldBbox} />
          </MapControls>

          {!!feature && (
            <OpenLayersFeatureLayer
              featureCollection={{
                type: 'FeatureCollection',
                features: [feature],
              }}
              style={genericOpenLayersFeatureStyle}
            />
          )}
          <OpenLayersLayer {...baseLayer} />

          <Box
            data-testid="area-filter-tile-license"
            sx={{
              zIndex: 10,
              justifySelf: 'center',
              bottom: '6px',
              left: '6px',
              position: 'absolute',
            }}
          >
            <Typography
              data-testid="area-filter-tile-license-typography"
              sx={{
                fontSize: 9,
                color: '#051039',
                backgroundColor: 'rgba(236, 237, 238, 0.7)',
                borderRadius: 1,
                px: 0.5,
                py: 0.25,
              }}
            >
              {tileLicense}
            </Typography>
          </Box>
        </OpenLayersMapView>
      </Box>
    </Box>
  );
};
